import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Chip,
  Box,
  FormControlLabel,
  CircularProgress,
  Button,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  Divider,
} from "@material-ui/core";

import SaveIcon from "@material-ui/icons/Save";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SettingsIcon from "@material-ui/icons/Settings";
import BusinessIcon from "@material-ui/icons/Business";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/Phone";

import { Toast } from "components/shared/Toaster";
import ChipInput from "components/shared/ChipInput";

import {
  statesOptions,
  businessTypeOption,
  industryTypeOption,
} from "constants/constantVariables";

import {
  getMarketplaceCriteria,
  createMarketplaceCriteria,
  updateMarketplaceCriteria,
} from "actions/user";

/* -------------------------------------------------------------------------- */
/*                                   HELPERS                                  */
/* -------------------------------------------------------------------------- */

const DEFAULT_LONGEVITY_POLICY = {
  enabled: false,
  minMonthsActive: null,
  missingStrategy: null,
};

const initialForm = {
  active: false,
  excludedStates: [],
  excludedZipCodes: [],
  excludedIndustryTypes: [],
  excludedBusinessTypes: [],
  longevityPolicy: DEFAULT_LONGEVITY_POLICY,
  requiredBusinessEmail: false,
  requiredBusinessPhone: false,
};

const normalizeForm = (data = {}) => ({
  active: !!data.active,
  excludedStates: data.excludedStates || [],
  excludedZipCodes: data.excludedZipCodes || [],
  excludedIndustryTypes: data.excludedIndustryTypes || [],
  excludedBusinessTypes: data.excludedBusinessTypes || [],
  longevityPolicy: {
    enabled: !!data.longevityPolicy?.enabled,
    minMonthsActive: data.longevityPolicy?.minMonthsActive ?? null,
    missingStrategy: data.longevityPolicy?.missingStrategy ?? null,
  },
  requiredBusinessEmail: !!data.requiredBusinessEmail,
  requiredBusinessPhone: !!data.requiredBusinessPhone,
});

/* -------------------------------------------------------------------------- */
/*                               UI COMPONENTS                                */
/* -------------------------------------------------------------------------- */

const SectionCard = ({ title, icon, children }) => (
  <Paper
    elevation={1}
    style={{
      padding: 16,
      marginBottom: 16,
      borderRadius: 8,
      border: "1px solid #e3f2fd",
      backgroundColor: "var(--bg-canvas)",
    }}
  >
    <Box display="flex" alignItems="center" mb={2}>
      {icon && <Box mr={1} color="primary.main">{icon}</Box>}
      <Typography
        variant="h6"
        style={{ fontWeight: 600, color: "var(--rox-lilac-ink)" }}
      >
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

const ChipSelectInput = ({
  label,
  options,
  values,
  onChange,
  placeholder = "Type to add",
}) => {
  const [input, setInput] = useState("");

  const filteredOptions = options.filter(
    (opt) =>
      opt.label.toLowerCase().includes(input.toLowerCase()) &&
      !values.includes(opt.value),
  );

  const addValue = (value) => {
    onChange([...values, value]);
    setInput("");
  };

  return (
    <Box mb={2}>
      <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 8, color: "#555" }}>
        {label}
      </Typography>

      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder={placeholder}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        style={{ marginBottom: 8 }}
      />

      {input && filteredOptions.length > 0 && (
        <Box
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: 4,
            maxHeight: 120,
            overflowY: "auto",
            background: "#fff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {filteredOptions.map((opt) => (
            <Box
              key={opt.value}
              px={2}
              py={1}
              style={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "var(--bg-canvas)" }
              }}
              onClick={() => addValue(opt.value)}
            >
              <Typography variant="body2">{opt.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box display="flex" flexWrap="wrap" gap={4} mt={1}>
        {values.map((value) => {
          const label = options.find((o) => o.value === value)?.label || value;

          return (
            <Chip
              key={value}
              label={label}
              onDelete={() => onChange(values.filter((v) => v !== value))}
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        })}
      </Box>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*                               MAIN COMPONENT                               */
/* -------------------------------------------------------------------------- */

const MarketplaceCriteriaForm = ({
  value,
  isActive,
  onUpdateActive,
  onUpdatePolicy,
  onUpdateFullPolicy,
  hideGlobalControls = false,
  hideSubmitButton = false,
  onSave
}) => {
  const [form, setForm] = useState(normalizeForm(value));
  const [loading, setLoading] = useState(false);
  const [existingCriteria, setExistingCriteria] = useState(false);

  // Update form when value prop changes
  useEffect(() => {
    if (value) {
      setForm(normalizeForm(value));
    }
  }, [value]);

  useEffect(() => {
    // Only fetch data if not using controlled mode
    if (!hideSubmitButton) {
      setLoading(true);

      getMarketplaceCriteria().then((res) => {
        const criteria = res?.response?.marketPlacePolicy?.generalPolicy || null;

        if (criteria) {
          setForm(
            normalizeForm({
              ...criteria,
              active: res?.response?.active,
            }),
          );
          setExistingCriteria(true);
        }

        setLoading(false);
      }).finally(() => setLoading(false));
    }
  }, [existingCriteria, hideSubmitButton]);

  const updateForm = useCallback((patch) => {
    const newForm = { ...form, ...patch };
    setForm(newForm);

    // If in controlled mode, notify parent of changes
    if (hideSubmitButton && onUpdateFullPolicy) {
      onUpdateFullPolicy(newForm);
    } else if (hideSubmitButton && onUpdatePolicy) {
      // Notify parent of individual field changes
      Object.entries(patch).forEach(([key, value]) => {
        onUpdatePolicy(key, value);
      });
    }
  }, [form, hideSubmitButton, onUpdatePolicy, onUpdateFullPolicy]);

  const handleSave = () => {
    // Only handle save if not in controlled mode
    if (hideSubmitButton) return;

    const payload = {
      active: form.active,
      marketPlacePolicy: {
        generalPolicy: { ...form },
      },
    };

    const action = existingCriteria
      ? updateMarketplaceCriteria
      : createMarketplaceCriteria;

    action(payload).then((res) => {
      Toast({
        type: res ? "success" : "error",
        message: res
          ? "Marketplace criteria saved successfully"
          : "Error saving marketplace criteria",
      });
      if(res){
        setExistingCriteria(true);
      }
    });

    if (onSave) {
      onSave(payload);
    }
  };

  // Handle active toggle for controlled mode
  const handleActiveToggle = (checked) => {
    updateForm({ active: checked });
    if (hideSubmitButton && onUpdateActive) {
      onUpdateActive(checked);
    }
  };

  if (loading && !hideSubmitButton) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={300}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* GLOBAL STATUS - Only show if not hiding global controls */}
      {!hideGlobalControls && (
        <SectionCard title="Global Status" icon={<CheckCircleIcon />}>
          <FormControlLabel
            label="Marketplace Active"
            control={
              <Switch
                color="primary"
                checked={hideSubmitButton ? isActive : form.active}
                onChange={(e) => handleActiveToggle(e.target.checked)}
              />
            }
          />
          <Typography variant="caption" color="textSecondary" display="block" mt={1}>
            {(hideSubmitButton ? isActive : form.active) ? "Marketplace policies are active and enforced" : "Marketplace policies are currently disabled"}
          </Typography>
        </SectionCard>
      )}

      {/* EXCLUSIONS */}
      <SectionCard title="Exclusion Policies" icon={<SettingsIcon />}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChipSelectInput
              label="Excluded States"
              options={statesOptions.USA}
              values={form.excludedStates}
              onChange={(v) => updateForm({ excludedStates: v })}
            />

            <ChipInput
              label="Excluded Zip Codes"
              values={form.excludedZipCodes}
              onChange={(v) => updateForm({ excludedZipCodes: v })}
              placeholder="Enter zip codes"
              helperText="Type a zip code and select from the dropdown to add it"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChipSelectInput
              label="Excluded Industries"
              options={industryTypeOption}
              values={form.excludedIndustryTypes}
              onChange={(v) => updateForm({ excludedIndustryTypes: v })}
            />

            <ChipSelectInput
              label="Excluded Business Types"
              options={businessTypeOption}
              values={form.excludedBusinessTypes}
              onChange={(v) => updateForm({ excludedBusinessTypes: v })}
            />
          </Grid>
        </Grid>
      </SectionCard>

      {/* LONGEVITY */}
      <SectionCard title="Business Longevity Policy" icon={<BusinessIcon />}>
        <FormControlLabel
          label="Enable Longevity Checks"
          control={
            <Switch
              color="primary"
              checked={form.longevityPolicy.enabled}
              onChange={(e) =>
                updateForm({
                  longevityPolicy: {
                    ...form.longevityPolicy,
                    enabled: e.target.checked,
                  },
                })
              }
            />
          }
        />
        <Typography variant="caption" color="textSecondary" display="block" mb={2}>
          Filter applications based on business establishment duration
        </Typography>

        {form.longevityPolicy.enabled && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Minimum Months Active"
                variant="outlined"
                size="small"
                type="number"
                placeholder="e.g. 12"
                value={form.longevityPolicy.minMonthsActive || ""}
                onChange={(e) =>
                  // console.log("minMonthsActive", e.target.value)
                  updateForm({
                    longevityPolicy: {
                      ...form.longevityPolicy,
                      minMonthsActive: e.target.value
                        ? parseInt(e.target.value, 10)
                        : null,
                    },
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel id="missing-strategy-label" shrink>
                  Missing Data Strategy
                </InputLabel>
                <Select
                  labelId="missing-strategy-label"
                  label="Missing Data Strategy"
                  value={form.longevityPolicy.missingStrategy || ""}
                  onChange={(e) =>
                    updateForm({
                      longevityPolicy: {
                        ...form.longevityPolicy,
                        missingStrategy: e.target.value,
                      },
                    })
                  }
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Strategy...
                  </MenuItem>
                  <MenuItem value="REJECT">Reject Application</MenuItem>
                  <MenuItem value="IGNORE">Ignore and Continue</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </SectionCard>

      {/* CONTACT REQUIREMENTS */}
      <SectionCard title="Contact Requirements" icon={<EmailIcon />}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              label="Business Email Required"
              control={
                <Switch
                  color="primary"
                  checked={form.requiredBusinessEmail}
                  onChange={(e) =>
                    updateForm({
                      requiredBusinessEmail: e.target.checked,
                    })
                  }
                />
              }
            />
            <Typography variant="caption" color="textSecondary" display="block">
              Require valid business email address
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              label="Business Phone Required"
              control={
                <Switch
                  color="primary"
                  checked={form.requiredBusinessPhone}
                  onChange={(e) =>
                    updateForm({
                      requiredBusinessPhone: e.target.checked,
                    })
                  }
                />
              }
            />
            <Typography variant="caption" color="textSecondary" display="block">
              Require valid business phone number
            </Typography>
          </Grid>
        </Grid>
      </SectionCard>

      {/* SAVE BUTTON - Only show if not hiding submit button */}
      {!hideSubmitButton && (
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Marketplace Criteria"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MarketplaceCriteriaForm;
